import api, { ClientServiceProvider } from 'api';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { LTHeader, LTItem, LTRow, LTPlaceholder, LTMobileItem } from 'components/common/ListTable';
import { RootState } from 'redux/store';
import { CustomLTRow } from '../../../../../customLTRow/customLTRow';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import ForwardToInboxRoundedIcon from '@mui/icons-material/ForwardToInboxRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import * as S from '../../../../../Table.styled';
import AlertMessage, { AlertObj } from 'components/common/alert';
import { useTranslation } from 'react-i18next';
import ToggleableDropdown from 'components/common/ToggleableDropdown/ToggleableDropdown';
import OptionDropdown, { DropdownOption } from 'components/common/OptionDropdown/OptionDropdown';
import { useAuth } from 'contexts/ProtectedRoute';
import { canEditUserProfile, canTransferClientOrganization, Role } from 'data/constants/roles';
import EditUserProfileModal, {
    EditableUserSummary,
} from 'views/ProviderProfile/AdminPanel/components/EditUserProfileModal';
import PermissionsModal from 'views/ProviderProfile/AdminPanel/components/PermissionsModal';
import TransferClientOrganizationModal from './TransferClientOrganizationModal';

export type ClientsTableClient = {
    id: number;
    external_id: string;
    service_provider_id: number | null;
    organization_name: string | null;
    firstname: string;
    lastname: string;
    email: string;
    phone_number: string | null;
    completed_assessment: string | null;
    status: string;
};

type ClientsTableProps = {
    clients: ClientsTableClient[];
    mobile: boolean;
    isLoading?: boolean;
    organizationFilterOptions: DropdownOption[];
    selectedOrganizationFilter: string;
    onOrganizationFilterChange: (value: string) => void;
    onClientUpdated?: () => void;
    showOrganizationColumn?: boolean;
};

type PendingClientTransfer = {
    client: ClientsTableClient;
    targetOrganizationId: string;
    targetOrganizationName: string;
};

const ClientsTable = ({
    clients,
    mobile,
    isLoading = false,
    organizationFilterOptions,
    selectedOrganizationFilter,
    onOrganizationFilterChange,
    onClientUpdated,
    showOrganizationColumn = true,
}: ClientsTableProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { role } = useAuth();
    const [isShowingAlert, setShowingAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<AlertObj>({ type: 'info', value: '' });
    const [selectedClientForEdit, setSelectedClientForEdit] = useState<EditableUserSummary | null>(
        null
    );
    const [selectedClientForPermissions, setSelectedClientForPermissions] =
        useState<EditableUserSummary | null>(null);
    const [transferOrganizations, setTransferOrganizations] = useState<ClientServiceProvider[]>([]);
    const [pendingTransfer, setPendingTransfer] = useState<PendingClientTransfer | null>(null);

    const canTransferClients = canTransferClientOrganization(role);

    // Clients in this table are external clients. Use that role for the
    // permission check that drives whether the edit icon is shown.
    const canEditClientProfiles = canEditUserProfile(role, Role.EXTERNAL_CLIENT);

    useEffect(() => {
        if (!showOrganizationColumn || !canTransferClients) {
            setTransferOrganizations([]);
            return;
        }

        let isCancelled = false;

        const loadTransferOrganizations = async () => {
            try {
                const response = await api.clientServiceProviders.getClientServiceProviders();
                if (!isCancelled) {
                    setTransferOrganizations(response.data || []);
                }
            } catch (error) {
                if (!isCancelled) {
                    console.error(error);
                    setTransferOrganizations([]);
                }
            }
        };

        loadTransferOrganizations();

        return () => {
            isCancelled = true;
        };
    }, [canTransferClients, showOrganizationColumn]);

    const getOrganizationLabel = useCallback((organization: ClientServiceProvider) => {
        return (
            organization.company_name ||
            organization.legal_entity_name ||
            organization.email ||
            `Organization #${organization.id}`
        );
    }, []);

    const getOrganizationOptionsForClient = useCallback(
        (client: ClientsTableClient): DropdownOption[] => {
            const currentOrganizationId = client.service_provider_id?.toString();
            const options: DropdownOption[] = [];

            if (currentOrganizationId) {
                options.push({
                    value: currentOrganizationId,
                    label: `${client.organization_name || currentOrganizationId} (${t('currentOrganization', { lng: language, defaultValue: 'Current organization' })})`,
                    disabled: true,
                });
            }

            transferOrganizations.forEach((organization) => {
                const organizationId = organization.id.toString();
                if (organizationId === currentOrganizationId) {
                    return;
                }

                options.push({
                    value: organizationId,
                    label: getOrganizationLabel(organization),
                });
            });

            return options;
        },
        [getOrganizationLabel, language, t, transferOrganizations]
    );

    const handleOrganizationSelect = (client: ClientsTableClient, organizationId: string) => {
        const currentOrganizationId = client.service_provider_id?.toString();
        if (!organizationId || organizationId === currentOrganizationId) {
            return;
        }

        const targetOrganization = getOrganizationOptionsForClient(client).find(
            (option) => option.value === organizationId && !option.disabled
        );
        if (!targetOrganization) {
            return;
        }

        setPendingTransfer({
            client,
            targetOrganizationId: organizationId,
            targetOrganizationName: targetOrganization.label,
        });
    };

    const renderOrganizationCell = (client: ClientsTableClient) => {
        if (!canTransferClients) {
            return client.organization_name || '--';
        }

        const organizationOptions = getOrganizationOptionsForClient(client);

        return (
            <OptionDropdown
                options={organizationOptions}
                value={client.service_provider_id?.toString() || ''}
                onChange={(organizationId) => handleOrganizationSelect(client, organizationId)}
                placeholder={client.organization_name || '--'}
                width={260}
                alignLeft={true}
            />
        );
    };

    const skeletonArray = clients.length > 0 ? clients : [1, 2, 3];
    const desktopTableMinWidth = '100%';
    const usersGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: showOrganizationColumn
            ? '150px minmax(220px, 1.2fr) minmax(260px, 1.4fr) 270px 160px 110px 150px 80px'
            : '150px minmax(220px, 1.2fr) minmax(260px, 1.4fr) 160px 110px 150px 80px',
        alignItems: 'center',
        width: desktopTableMinWidth,
    };

    const toEditableUser = (client: ClientsTableClient): EditableUserSummary => ({
        id: client.id,
        firstname: client.firstname,
        lastname: client.lastname,
        email: client.email,
        external_id: client.external_id,
    });

    const handleInvitation = async (userId: number) => {
        try {
            const request = await api.sendInvitation.sendInvitation(userId);
            if (request.status === 200 || request.status === 201) {
                setAlertMessage({
                    type: 'success',
                    value: t('invitationSuccessfullySent', { lng: language }),
                });
                setShowingAlert(true);
            }
        } catch {
            setAlertMessage({ type: 'error', value: t('somethingWentWrong', { lng: language }) });
            setShowingAlert(true);
        }
    };

    const Alert = (
        <AlertMessage
            type={alertMessage?.type}
            text={alertMessage?.value}
            open={isShowingAlert}
            onClose={() => setShowingAlert(false)}
        ></AlertMessage>
    );

    return (
        <>
            {Alert}
            <EditUserProfileModal
                open={Boolean(selectedClientForEdit)}
                user={selectedClientForEdit}
                onClose={() => setSelectedClientForEdit(null)}
                onSaved={() => {
                    onClientUpdated?.();
                }}
            />
            <PermissionsModal
                open={Boolean(selectedClientForPermissions)}
                user={selectedClientForPermissions}
                onClose={() => setSelectedClientForPermissions(null)}
            />
            {pendingTransfer && (
                <TransferClientOrganizationModal
                    open={Boolean(pendingTransfer)}
                    clientId={pendingTransfer.client.id}
                    clientName={`${pendingTransfer.client.firstname} ${pendingTransfer.client.lastname}`}
                    currentOrganizationName={pendingTransfer.client.organization_name || '--'}
                    targetOrganizationName={pendingTransfer.targetOrganizationName}
                    targetServiceProviderId={Number(pendingTransfer.targetOrganizationId)}
                    onClose={() => setPendingTransfer(null)}
                    onSuccess={() => {
                        setAlertMessage({
                            type: 'success',
                            value: t('clientTransferSuccess', {
                                lng: language,
                                defaultValue: 'Client transferred successfully.',
                            }),
                        });
                        setShowingAlert(true);
                        onClientUpdated?.();
                    }}
                    onInfo={(message) => {
                        setAlertMessage({ type: 'info', value: message });
                        setShowingAlert(true);
                    }}
                />
            )}
            <S.ListTable
                style={
                    mobile
                        ? { maxHeight: 'unset', width: '100%', maxWidth: '900px' }
                        : { maxHeight: 'unset', width: '97vw', marginTop: '36px' }
                }
            >
                <LTHeader
                    style={{ paddingLeft: '16px', minWidth: desktopTableMinWidth }}
                    innerStyle={mobile ? undefined : usersGridStyle}
                >
                    <LTItem style={mobile ? undefined : { width: '100%' }}>External ID</LTItem>
                    <LTItem style={mobile ? undefined : { width: '100%' }}>
                        {t('name', { lng: language })}
                    </LTItem>
                    <LTItem style={mobile ? undefined : { width: '100%' }}>
                        {t('email', { lng: language })}
                    </LTItem>
                    {showOrganizationColumn && (
                        <LTItem
                            style={
                                mobile
                                    ? undefined
                                    : { width: '100%', display: 'flex', alignItems: 'center' }
                            }
                        >
                            {t('organization', { lng: language })}
                            <ToggleableDropdown
                                selectedType={selectedOrganizationFilter}
                                handleSelectedType={onOrganizationFilterChange}
                                dropdownMenuPrograms={[
                                    {
                                        label: t('all', { lng: language }),
                                        value: t('all', { lng: language }),
                                    },
                                    ...organizationFilterOptions,
                                ]}
                                purple={true}
                                label="selectCase"
                            />
                        </LTItem>
                    )}
                    <LTItem style={mobile ? undefined : { width: '100%' }}>
                        {t('phoneNumber', { lng: language })}
                    </LTItem>
                    <LTItem
                        style={
                            mobile
                                ? undefined
                                : { width: '100%', whiteSpace: 'normal', lineHeight: 1.2 }
                        }
                    >
                        {t('assessmentCompleted', { lng: language })}
                    </LTItem>
                    <LTItem style={mobile ? undefined : { width: '100%', textAlign: 'center' }}>
                        {t('status', { lng: language, defaultValue: 'Status' })}
                    </LTItem>
                    <LTItem
                        style={
                            mobile
                                ? undefined
                                : {
                                      width: '100%',
                                      textAlign: 'center',
                                      whiteSpace: 'normal',
                                      lineHeight: 1.2,
                                  }
                        }
                    >
                        {t('permissions', { lng: language })}
                    </LTItem>
                </LTHeader>

                {!isLoading && clients.length === 0 && (
                    <LTPlaceholder>{t('theListIsEmpty', { lng: language })}</LTPlaceholder>
                )}

                {!isLoading &&
                    clients.length > 0 &&
                    clients.map((client) => (
                        <CustomLTRow
                            key={`client_${client.id}`}
                            id={`client_${client.id}`}
                            style={
                                mobile
                                    ? { paddingLeft: '16px' }
                                    : {
                                          paddingLeft: '16px',
                                          width: desktopTableMinWidth,
                                          minWidth: desktopTableMinWidth,
                                          maxWidth: desktopTableMinWidth,
                                      }
                            }
                            wrapperStyle={mobile ? undefined : usersGridStyle}
                        >
                            <LTItem style={mobile ? undefined : { width: '100%' }}>
                                {client.external_id}
                            </LTItem>
                            <LTItem
                                tip={`${client.firstname} ${client.lastname}`}
                                style={mobile ? undefined : { width: '100%' }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        minWidth: 0,
                                    }}
                                >
                                    {canEditClientProfiles && (
                                        <IconButton
                                            size="small"
                                            title={t('editUser', {
                                                lng: language,
                                                defaultValue: 'Edit user',
                                            })}
                                            onClick={() =>
                                                setSelectedClientForEdit(toEditableUser(client))
                                            }
                                            sx={{
                                                padding: '2px',
                                                color: '#6d41d8',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(131, 83, 226, 0.08)',
                                                },
                                            }}
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    )}
                                    <S.Link to={`/dashboard/${client.id}/client-profile`}>
                                        {`${client.firstname} ${client.lastname}`}
                                    </S.Link>
                                </div>
                            </LTItem>
                            <LTItem
                                tip={client.email}
                                style={mobile ? undefined : { width: '100%' }}
                            >
                                {client.email}
                            </LTItem>
                            {showOrganizationColumn && (
                                <LTItem style={mobile ? undefined : { width: '100%' }}>
                                    {renderOrganizationCell(client)}
                                </LTItem>
                            )}
                            <LTItem style={mobile ? undefined : { width: '100%' }}>
                                {client.phone_number || '--'}
                            </LTItem>
                            <LTItem style={mobile ? undefined : { width: '100%' }}>
                                {client.completed_assessment || '--'}
                            </LTItem>
                            <LTItem
                                style={mobile ? undefined : { width: '100%', textAlign: 'center' }}
                            >
                                {(() => {
                                    const isRegistered =
                                        (client.status || '').toUpperCase() === 'REGISTERED';
                                    return (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            {isRegistered ? (
                                                <span
                                                    style={{
                                                        fontSize: '12px',
                                                        fontWeight: 500,
                                                        padding: '4px 10px',
                                                        borderRadius: '9999px',
                                                        backgroundColor: 'rgba(34, 197, 94, 0.12)',
                                                        color: '#15803d',
                                                    }}
                                                >
                                                    {t('registered', {
                                                        lng: language,
                                                        defaultValue: 'Registered',
                                                    })}
                                                </span>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    title={t('resend', {
                                                        lng: language,
                                                        defaultValue: 'Resend invitation',
                                                    })}
                                                    onClick={() => handleInvitation(client.id)}
                                                    sx={{
                                                        padding: '4px',
                                                        color: '#6d41d8',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(131, 83, 226, 0.08)',
                                                        },
                                                    }}
                                                >
                                                    <ForwardToInboxRoundedIcon
                                                        sx={{ fontSize: 18 }}
                                                    />
                                                </IconButton>
                                            )}
                                        </div>
                                    );
                                })()}
                            </LTItem>
                            <LTItem
                                style={mobile ? undefined : { width: '100%', textAlign: 'center' }}
                            >
                                <IconButton
                                    size="small"
                                    title={t('permissions', { lng: language })}
                                    onClick={() =>
                                        setSelectedClientForPermissions(toEditableUser(client))
                                    }
                                    sx={{
                                        padding: '4px',
                                        border: '1px solid rgba(131, 83, 226, 0.18)',
                                        borderRadius: '10px',
                                        color: '#6d41d8',
                                        backgroundColor: 'rgba(131, 83, 226, 0.08)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(131, 83, 226, 0.14)',
                                        },
                                    }}
                                >
                                    <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </LTItem>

                            <LTMobileItem
                                style={{ minWidth: '132px' }}
                                name={t('name', { lng: language })}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    {canEditClientProfiles && (
                                        <IconButton
                                            size="small"
                                            title={t('editUser', {
                                                lng: language,
                                                defaultValue: 'Edit user',
                                            })}
                                            onClick={() =>
                                                setSelectedClientForEdit(toEditableUser(client))
                                            }
                                            sx={{
                                                padding: '2px',
                                                color: '#6d41d8',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(131, 83, 226, 0.08)',
                                                },
                                            }}
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    )}
                                    <S.Link to={`/dashboard/${client.id}/client-profile`}>
                                        {`${client.firstname} ${client.lastname}`}
                                    </S.Link>
                                </div>
                            </LTMobileItem>
                            <LTMobileItem style={{ minWidth: '132px' }} name="External ID">
                                {client.external_id}
                            </LTMobileItem>
                            <LTMobileItem
                                style={{ minWidth: '132px' }}
                                name={t('email', { lng: language })}
                            >
                                {client.email}
                            </LTMobileItem>
                            {showOrganizationColumn && (
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('organization', { lng: language })}
                                >
                                    {renderOrganizationCell(client)}
                                </LTMobileItem>
                            )}
                            <LTMobileItem
                                style={{ minWidth: '132px' }}
                                name={t('phoneNumber', { lng: language })}
                            >
                                {client.phone_number || '--'}
                            </LTMobileItem>
                            <LTMobileItem
                                style={{ minWidth: '132px' }}
                                name={t('assessmentCompleted', { lng: language })}
                            >
                                {client.completed_assessment || '--'}
                            </LTMobileItem>
                            <LTMobileItem
                                style={{ minWidth: '132px' }}
                                name={t('status', { lng: language, defaultValue: 'Status' })}
                            >
                                {(() => {
                                    const isRegistered =
                                        (client.status || '').toUpperCase() === 'REGISTERED';
                                    return (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            {isRegistered ? (
                                                <span
                                                    style={{
                                                        fontSize: '12px',
                                                        fontWeight: 500,
                                                        padding: '4px 10px',
                                                        borderRadius: '9999px',
                                                        backgroundColor: 'rgba(34, 197, 94, 0.12)',
                                                        color: '#15803d',
                                                    }}
                                                >
                                                    {t('registered', {
                                                        lng: language,
                                                        defaultValue: 'Registered',
                                                    })}
                                                </span>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    title={t('resend', {
                                                        lng: language,
                                                        defaultValue: 'Resend invitation',
                                                    })}
                                                    onClick={() => handleInvitation(client.id)}
                                                    sx={{
                                                        color: '#6d41d8',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(131, 83, 226, 0.08)',
                                                        },
                                                    }}
                                                >
                                                    <ForwardToInboxRoundedIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </div>
                                    );
                                })()}
                            </LTMobileItem>
                            <LTMobileItem
                                style={{ minWidth: '132px' }}
                                name={t('permissions', { lng: language })}
                            >
                                <IconButton
                                    size="small"
                                    title={t('permissions', { lng: language })}
                                    onClick={() =>
                                        setSelectedClientForPermissions(toEditableUser(client))
                                    }
                                    sx={{
                                        padding: '4px',
                                        border: '1px solid rgba(131, 83, 226, 0.18)',
                                        borderRadius: '10px',
                                        color: '#6d41d8',
                                        backgroundColor: 'rgba(131, 83, 226, 0.08)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(131, 83, 226, 0.14)',
                                        },
                                    }}
                                >
                                    <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </LTMobileItem>
                        </CustomLTRow>
                    ))}

                {isLoading &&
                    skeletonArray.map((_, idx) => (
                        <LTRow key={idx} style={{ height: '44px' }}>
                            <LTItem minWidth="25%">
                                <Skeleton width={140} />
                            </LTItem>
                            <LTItem minWidth="30%">
                                <Skeleton width={200} />
                            </LTItem>
                            <LTItem minWidth="15%">
                                <Skeleton width={90} />
                            </LTItem>
                            <LTItem minWidth="20%" lastItem>
                                <Skeleton width={120} />
                            </LTItem>
                        </LTRow>
                    ))}
            </S.ListTable>
        </>
    );
};

export default ClientsTable;
